import {
  computed, ref, Ref,
} from 'vue';
import { useStore } from '@/store';
import { UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import {
  RcsAgent,
  RcsOnboardingStatus,
  RcsOnboardingStatusPayload,
  RcsSelectableSender,
  normalizeRcsTestDevicePhoneNumber,
} from '@/types/rcs-onboarding';

export interface RcsTestSendSuggestion {
  key: string;
  value: string;
  country?: string;
}

export interface RcsTemplateTestPayload {
  type: 'richtexting';
  params: RcsTestSendSuggestion | RcsTestSendSuggestion[];
  contactId?: number;
  langs?: string[];
  conditionOverrides?: Record<string, boolean>;
  senderId?: string;
  informations?: {
    rcsSenderId?: string;
  };
}

/**
 * Shared state for RCS test sends via SendTestV4Modal (onboarding status,
 * agent selection, test device numbers).
 */
export function useRcsTestSend(shopIdRef?: Ref<number>) {
  const store = useStore();
  const status = ref<RcsOnboardingStatusPayload | null>(null);
  const isLoading = ref(false);
  const loadError = ref<string | null>(null);

  const selectedSenderId = ref<string>('');
  const selectedTestNumbers = ref<string[]>([]);
  const sessionExtraNumbers = ref<string[]>([]);

  const resolvedShopId = computed(() => {
    if (shopIdRef?.value) return shopIdRef.value;
    return UserState.activeShop?.id ?? 0;
  });

  const sharedSender = computed<RcsSelectableSender | null>(
    () => status.value?.shared_sender || null,
  );

  const agents = computed<RcsAgent[]>(() => status.value?.agents || []);

  const agentsBySenderId = computed(() => {
    const map = new Map<string, RcsAgent>();
    agents.value.forEach((agent) => {
      if (agent.sender_id) {
        map.set(agent.sender_id, agent);
      }
    });
    return map;
  });

  const launchedSenderIds = computed(() => new Set(
    agents.value
      .filter((agent) => agent.onboarding_status === RcsOnboardingStatus.LAUNCHED)
      .map((agent) => agent.sender_id),
  ));

  const selectableDedicatedAgents = computed<RcsSelectableSender[]>(() => (
    (status.value?.selectable_senders || []).filter(
      (sender) => sender.source === 'dedicated'
        && launchedSenderIds.value.has(sender.sender_id),
    )
  ));

  const allSenderOptions = computed<RcsSelectableSender[]>(() => {
    const options: RcsSelectableSender[] = [];
    if (sharedSender.value) {
      options.push(sharedSender.value);
    }
    options.push(...selectableDedicatedAgents.value);
    return options;
  });

  const selectedSender = computed<RcsSelectableSender | null>(() => (
    allSenderOptions.value.find((sender) => sender.sender_id === selectedSenderId.value) || null
  ));

  const isSharedSenderSelected = computed(
    () => selectedSender.value?.source === 'shared',
  );

  const selectedAgent = computed<RcsAgent | null>(() => (
    agentsBySenderId.value.get(selectedSenderId.value) || null
  ));

  const persistedTestNumbers = computed<string[]>(() => {
    const numbers = selectedAgent.value?.test_numbers?.numbers || [];
    return numbers
      .map((entry) => normalizeRcsTestDevicePhoneNumber(entry.number || ''))
      .filter(Boolean);
  });

  const availableTestNumbers = computed<string[]>(() => {
    const merged = new Set<string>([
      ...persistedTestNumbers.value,
      ...sessionExtraNumbers.value,
    ]);
    return Array.from(merged);
  });

  const canPersistTestNumbers = computed(
    () => !!selectedAgent.value?.local_id
      && selectedAgent.value?.onboarding_status === RcsOnboardingStatus.LAUNCHED,
  );

  const refresh = async (): Promise<void> => {
    if (!resolvedShopId.value) return;
    isLoading.value = true;
    loadError.value = null;
    try {
      const response = await nestCall(
        'get',
        NestApiType.MESSAGING,
        '/rcs/onboarding/status',
        {},
        '',
        undefined,
        true,
        { shop_id: Number(resolvedShopId.value) },
      );
      const data = response?.data?.data || response?.data;
      status.value = data as RcsOnboardingStatusPayload;
    } catch (err: any) {
      loadError.value = err?.response?.data?.code || 'rcs_status_fetch_failed';
      status.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  const applyInitialSenderId = (initialSenderId?: string | null) => {
    if (initialSenderId && allSenderOptions.value.some((s) => s.sender_id === initialSenderId)) {
      selectedSenderId.value = initialSenderId;
      return;
    }
    if (sharedSender.value?.sender_id) {
      selectedSenderId.value = sharedSender.value.sender_id;
      return;
    }
    if (selectableDedicatedAgents.value[0]?.sender_id) {
      selectedSenderId.value = selectableDedicatedAgents.value[0].sender_id;
    }
  };

  const prepareForModal = async (initialSenderId?: string | null) => {
    await refresh();
    applyInitialSenderId(initialSenderId);
    selectedTestNumbers.value = [];
    sessionExtraNumbers.value = [];
  };

  const toggleTestNumber = (number: string) => {
    const normalized = normalizeRcsTestDevicePhoneNumber(number);
    if (!normalized) return;
    const index = selectedTestNumbers.value.indexOf(normalized);
    if (index >= 0) {
      selectedTestNumbers.value = selectedTestNumbers.value.filter((n) => n !== normalized);
    } else {
      selectedTestNumbers.value = [...selectedTestNumbers.value, normalized];
    }
  };

  const addSessionTestNumber = (rawNumber: string): string | null => {
    const normalized = normalizeRcsTestDevicePhoneNumber(rawNumber);
    if (!normalized) return null;
    if (!sessionExtraNumbers.value.includes(normalized)) {
      sessionExtraNumbers.value = [...sessionExtraNumbers.value, normalized];
    }
    if (!selectedTestNumbers.value.includes(normalized)) {
      selectedTestNumbers.value = [...selectedTestNumbers.value, normalized];
    }
    return normalized;
  };

  const persistSessionTestNumbers = async (): Promise<void> => {
    if (!canPersistTestNumbers.value || !selectedAgent.value?.local_id) {
      return;
    }
    const numbers = availableTestNumbers.value.map((number) => ({ number }));
    await nestCall(
      'post',
      NestApiType.MESSAGING,
      '/rcs/onboarding/save-test-numbers',
      {},
      '',
      {
        shop_id: Number(resolvedShopId.value),
        agent_local_id: selectedAgent.value.local_id,
        test_numbers: { numbers },
      },
    );
    await refresh();
    applyInitialSenderId(selectedSenderId.value);
  };

  const buildRecipientParams = (
    recipient: RcsTestSendSuggestion | null,
    country?: string,
  ): RcsTestSendSuggestion[] => {
    const fromTestNumbers = selectedTestNumbers.value.map((number) => ({
      key: number,
      value: number.replace(/^\+/, ''),
      country,
    }));

    if (fromTestNumbers.length > 0) {
      return fromTestNumbers;
    }

    if (recipient?.value) {
      return [{
        ...recipient,
        country: recipient.country || country,
      }];
    }

    return [];
  };

  const buildTemplateTestPayload = (options: {
    params: RcsTestSendSuggestion | RcsTestSendSuggestion[];
    contactId?: number | null;
    langs?: string[];
    conditionOverrides?: Record<string, boolean>;
  }): RcsTemplateTestPayload => {
    const senderId = selectedSenderId.value || undefined;
    const payload: RcsTemplateTestPayload = {
      type: 'richtexting',
      params: options.params,
    };

    if (options.contactId) {
      payload.contactId = options.contactId;
    }
    if (Array.isArray(options.langs) && options.langs.length > 0) {
      payload.langs = options.langs;
    }
    if (options.conditionOverrides && Object.keys(options.conditionOverrides).length > 0) {
      payload.conditionOverrides = options.conditionOverrides;
    }
    if (senderId) {
      payload.senderId = senderId;
      payload.informations = { rcsSenderId: senderId };
    }

    return payload;
  };

  const openSendTestModal = (templateId: number, initialSenderId?: string | null) => {
    store.commit('general/showSendTestEmailV4Modal', templateId);
    prepareForModal(initialSenderId);
  };

  return {
    status,
    isLoading,
    loadError,
    sharedSender,
    selectableDedicatedAgents,
    allSenderOptions,
    agentsBySenderId,
    selectedSenderId,
    selectedSender,
    isSharedSenderSelected,
    selectedAgent,
    selectedTestNumbers,
    sessionExtraNumbers,
    availableTestNumbers,
    persistedTestNumbers,
    canPersistTestNumbers,
    refresh,
    prepareForModal,
    applyInitialSenderId,
    toggleTestNumber,
    addSessionTestNumber,
    persistSessionTestNumbers,
    buildRecipientParams,
    buildTemplateTestPayload,
    openSendTestModal,
  };
}
